import { Add, MedicalServices, MedicationOutlined, ReceiptLong } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useEffect, useState, type ReactNode } from "react";

import { CircularLoading } from "@/components/common";
import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { useDocumentDialog } from "@/hooks/useDocumentDialog";
import { MedicalNotes } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/MedicalNotes";
import { IframeDialog } from "@/pages/ehrrecords/components/IframeDialog";

import { NewMedication } from "../../NewMedication";
import { useMedicationControl } from "../../../../../hooks/useMedicationControl";
import { ControlPrescriptions } from "../../ControlPrescriptions";

type MedicationsTab = "prescriptions" | "manage";

const NEW_MEDICATION_FORM_ID = "control-medications-new-form";

interface ControlMedicationsProps {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  /** Conteúdo sem shell — o pai renderiza `ScreenMobile` (ex.: PatientDash mobile). */
  embedded?: boolean;
}

function MedicationsSegmentedTab({
  label,
  icon,
  count,
  selected,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      onClick={onClick}
      aria-selected={selected}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        py: 0.85,
        px: 1,
        border: "none",
        borderRadius: 99,
        cursor: "pointer",
        bgcolor: selected ? "background.paper" : "transparent",
        color: selected ? "primary.main" : "text.secondary",
        boxShadow: selected ? "0 1px 4px rgba(15, 23, 42, 0.08)" : "none",
        transition: "all 0.18s ease",
        outline: "none",
        minWidth: 0,
        "& svg": { fontSize: "1.05rem", flexShrink: 0 },
      }}
    >
      {icon}
      <Typography
        component="span"
        sx={{
          fontWeight: selected ? 700 : 500,
          fontSize: "0.8125rem",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.5,
          borderRadius: 99,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.68rem",
          fontWeight: 700,
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.grey[500], 0.15),
          color: selected ? "primary.main" : "text.disabled",
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

function MedicationsTabsBar({
  selectedTab,
  onSelectTab,
  manageCount,
  prescriptionCount,
}: {
  selectedTab: MedicationsTab;
  onSelectTab: (tab: MedicationsTab) => void;
  manageCount: number;
  prescriptionCount: number;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        p: 0.5,
        borderRadius: 99,
        bgcolor: alpha(theme.palette.grey[500], 0.1),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <MedicationsSegmentedTab
        label="Gerenciar"
        icon={<MedicationOutlined />}
        count={manageCount}
        selected={selectedTab === "manage"}
        onClick={() => onSelectTab("manage")}
      />
      <MedicationsSegmentedTab
        label="Receitas"
        icon={<ReceiptLong />}
        count={prescriptionCount}
        selected={selectedTab === "prescriptions"}
        onClick={() => onSelectTab("prescriptions")}
      />
    </Box>
  );
}

/** Conteúdo interno — abas Gerenciar / Receitas (mobile). */
function ControlMedicationsMobileBody({
  selectedTab,
  onSelectTab,
  manageCount,
  prescriptionCount,
  manageTabContent,
  prescriptionsContent,
}: {
  selectedTab: MedicationsTab;
  onSelectTab: (tab: MedicationsTab) => void;
  manageCount: number;
  prescriptionCount: number;
  manageTabContent: ReactNode;
  prescriptionsContent: ReactNode;
}) {
  return (
    <Stack width="100%" spacing={1.5} sx={{ pt: 0.5, minWidth: 0 }}>
      <MedicationsTabsBar
        selectedTab={selectedTab}
        onSelectTab={onSelectTab}
        manageCount={manageCount}
        prescriptionCount={prescriptionCount}
      />
      {selectedTab === "prescriptions" ? prescriptionsContent : manageTabContent}
    </Stack>
  );
}

export function ControlMedications({
  open,
  setOpen,
  embedded = false,
}: ControlMedicationsProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openNewMedication, setOpenNewMedication] = useState(false);
  const [selectedTab, setSelectedTab] = useState<MedicationsTab>("manage");

  const {
    open: iframeOpen,
    binaryUrl,
    setBinaryUrl,
    handleOpen,
    handleClose,
  } = useDocumentDialog();

  const {
    isInitialLoading,
    isRefetching,
    medications: medicationData,
    medicationRequestEntries,
    prescriptionDocuments,
    prescriptionRowData,
    prescriptionCount,
    refetch: setRefetchMedication,
  } = useMedicationControl();

  const manageCount = medicationData?.length ?? 0;

  useEffect(() => {
    if (open) {
      setSelectedTab("manage");
      setOpenNewMedication(false);
    }
  }, [open]);

  const handleOpenAddForm = () => {
    setSelectedTab("manage");
    setOpenNewMedication(true);
  };

  const handleCloseAddForm = () => {
    setOpenNewMedication(false);
  };

  const manageListContent = (
    <>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="small"
        startIcon={<Add />}
        onClick={handleOpenAddForm}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          py: 0.85,
          borderRadius: 2,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
      >
        Adicionar medicamento
      </Button>
      <Box sx={{ mt: 0.75 }}>
        <ControlPrescriptions
          loading={isInitialLoading}
          data={medicationData}
          setRefetch={() => setRefetchMedication()}
          isRefetching={isRefetching}
          prescriptionDocuments={prescriptionDocuments}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      </Box>
    </>
  );

  const prescriptionsContent = isInitialLoading ? (
    <Stack alignItems="center" justifyContent="center" py={4}>
      <CircularLoading />
    </Stack>
  ) : (
    <MedicalNotes
      sign
      rowData={prescriptionRowData}
      medicationRequest={medicationRequestEntries}
      handleOpen={handleOpen}
      setBinaryUrl={setBinaryUrl}
    />
  );

  const mobileBody = (
    <ControlMedicationsMobileBody
      selectedTab={selectedTab}
      onSelectTab={setSelectedTab}
      manageCount={manageCount}
      prescriptionCount={prescriptionCount}
      manageTabContent={manageListContent}
      prescriptionsContent={prescriptionsContent}
    />
  );

  const desktopBody = (
    <Stack width="100%" spacing={1.5}>
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MedicalServices sx={{ fontSize: 22, color: "primary.main" }} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: isSmallScreen ? "1rem" : "1.15rem",
              lineHeight: 1.3,
            }}
          >
            Meus Medicamentos
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            pl: 3.75,
          }}
        >
          Gerencie seus medicamentos
        </Typography>
      </Stack>

      {!openNewMedication && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="small"
          startIcon={<Add />}
          onClick={handleOpenAddForm}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            py: 0.85,
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Adicionar medicamento
        </Button>
      )}

      {openNewMedication ? (
        <NewMedication
          setModalOpen={setOpenNewMedication}
          setRefetch={() => setRefetchMedication()}
        />
      ) : (
        <Box sx={{ mt: 0.25 }}>
          <ControlPrescriptions
            loading={isInitialLoading}
            data={medicationData}
            setRefetch={() => setRefetchMedication()}
            isRefetching={isRefetching}
            prescriptionDocuments={prescriptionDocuments}
            handleOpen={handleOpen}
            setBinaryUrl={setBinaryUrl}
          />
        </Box>
      )}
    </Stack>
  );

  const overlays = (
    <>
      <MobileBottomSheet
        open={openNewMedication}
        onClose={handleCloseAddForm}
        title="Adicionar medicamento"
        subtitle="Informe os dados do medicamento"
        confirmLabel="Salvar"
        confirmType="submit"
        confirmForm={NEW_MEDICATION_FORM_ID}
        zIndexOffset={embedded ? 5 : 0}
      >
        <NewMedication
          hideActions
          formId={NEW_MEDICATION_FORM_ID}
          setModalOpen={setOpenNewMedication}
          setRefetch={() => setRefetchMedication()}
        />
      </MobileBottomSheet>

      {iframeOpen ? (
        <IframeDialog
          handleClose={handleClose}
          isOpen={iframeOpen}
          data={binaryUrl}
        />
      ) : null}
    </>
  );

  // Modo embutido: só o conteúdo (ScreenMobile fica no PatientDash).
  if (embedded) {
    return (
      <>
        {mobileBody}
        {overlays}
      </>
    );
  }

  // Mobile standalone (ex.: MoreOptions) — shell próprio.
  if (isMobileLayout) {
    return (
      <>
        <ScreenMobile
          open={!!open}
          onClose={() => setOpen?.(false)}
          title="Meus Medicamentos"
          subtitle="Gerencie seus medicamentos"
        >
          {mobileBody}
        </ScreenMobile>
        {overlays}
      </>
    );
  }

  // Desktop — dialog modal.
  return (
    <>
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: 0,
            maxWidth: "480px",
            width: "100%",
            height: "fit-content",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
        maxWidth="sm"
        fullWidth
        onClose={() => setOpen?.(false)}
        open={!!open}
      >
        <IconButton
          onClick={() => setOpen?.(false)}
          sx={{ position: "absolute", right: 8, top: 8, zIndex: 2 }}
          aria-label="Fechar"
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            pt: { xs: 4.5, sm: 2.5 },
            overflow: "auto",
            flex: 1,
          }}
        >
          {desktopBody}
        </DialogContent>
      </Dialog>
      {overlays}
    </>
  );
}
